<template>
  <section class="plan-confirm-page">
    <div class="container">
      <div class="plan-confirm-card">
        <div class="plan-confirm-icon" :class="'is-' + uiState">
          <i :class="iconClass"></i>
        </div>

        <h1 class="plan-confirm-title">{{ title }}</h1>
        <p class="plan-confirm-message">{{ message }}</p>
        <p v-if="uiState === 'active'" class="plan-confirm-note">
          Si no reconoces este correo, contacta a la clínica.
        </p>

        <div v-if="uiState === 'pending'" class="plan-confirm-pending">
          <b-spinner small class="mr-2" />
          Confirmando con el sistema de pagos…
        </div>

        <div class="plan-confirm-actions">
          <router-link
            v-if="uiState === 'active'"
            to="/login"
            class="btn btn-confirm-cta"
          >
            Ir a iniciar sesión
          </router-link>
          <router-link
            v-if="uiState === 'expired' || uiState === 'canceled' || uiState === 'timeout' || uiState === 'error'"
            :to="{ path: '/plans', hash: 'planes-2027' }"
            class="btn btn-confirm-cta"
          >
            Volver a planes
          </router-link>
          <router-link
            v-if="uiState === 'active'"
            :to="{ path: '/plans', hash: 'planes-2027' }"
            class="btn btn-confirm-ghost"
          >
            Ver planes
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import plansService from '@/core/services/api/landings/plans'

const POLL_INTERVAL_MS = 3000
const POLL_MAX_MS = 60000

/**
 * Página post-Stripe: consulta el estado de la sesión hasta active / timeout.
 * Ruta: /plan-confirmado?session_id=...
 */
export default {
  name: 'PlanConfirmado',
  components: { BSpinner },
  data() {
    return {
      sessionState: null,
      planName: '',
      emailHint: '',
      timedOut: false,
      fetchError: false,
      pollTimer: null,
      startedAt: 0
    }
  },
  computed: {
    sessionId() {
      return (this.$route.query && this.$route.query.session_id) || ''
    },
    uiState() {
      if (!this.sessionId) return 'error'
      if (this.fetchError && !this.sessionState) return 'error'
      if (this.sessionState === 'active') return 'active'
      if (this.sessionState === 'expired') return 'expired'
      if (this.sessionState === 'canceled') return 'canceled'
      if (this.timedOut) return 'timeout'
      return 'pending'
    },
    title() {
      const map = {
        pending: 'Estamos confirmando tu pago…',
        active: 'Pago recibido',
        expired: 'La sesión de pago expiró',
        canceled: 'Esta membresía fue cancelada',
        timeout: 'Tu pago se está procesando',
        error: 'No encontramos la sesión de pago'
      }
      return map[this.uiState] || map.pending
    },
    message() {
      if (this.uiState === 'active') {
        const plan = this.planName || 'tu plan'
        const email = this.emailHint || 'tu correo'
        return `Tu plan ${plan} está activo. Te enviamos el acceso a ${email}.`
      }
      if (this.uiState === 'expired') {
        return 'Puedes volver a elegir tu plan e intentarlo de nuevo.'
      }
      if (this.uiState === 'canceled') {
        return 'Si fue un error, vuelve a los planes e inicia la compra otra vez.'
      }
      if (this.uiState === 'timeout') {
        return 'Recibirás un correo cuando se confirme el pago.'
      }
      if (this.uiState === 'error') {
        return 'Abre el enlace desde Stripe o vuelve a los planes para comprar de nuevo.'
      }
      return 'Esto puede tomar unos segundos. No cierres esta ventana.'
    },
    iconClass() {
      const map = {
        pending: 'fas fa-sync-alt fa-spin',
        active: 'fas fa-check-circle',
        expired: 'fas fa-hourglass-end',
        canceled: 'fas fa-ban',
        timeout: 'fas fa-envelope',
        error: 'fas fa-exclamation-circle'
      }
      return map[this.uiState] || map.pending
    }
  },
  mounted() {
    if (!this.sessionId) return
    this.startedAt = Date.now()
    this.pollOnce()
  },
  beforeDestroy() {
    this.clearPoll()
  },
  methods: {
    clearPoll() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },
    scheduleNext() {
      this.clearPoll()
      const elapsed = Date.now() - this.startedAt
      if (elapsed >= POLL_MAX_MS) {
        this.timedOut = true
        return
      }
      this.pollTimer = setTimeout(() => {
        this.pollOnce()
      }, POLL_INTERVAL_MS)
    },
    async pollOnce() {
      try {
        const res = await plansService.getMemberPlanCheckoutSession(this.sessionId)
        const data = (res && res.data) || {}
        this.sessionState = data.state || null
        this.planName = data.plan_name || ''
        this.emailHint = data.email_hint || ''
        this.fetchError = false

        if (this.sessionState === 'pending') {
          this.scheduleNext()
          return
        }
        this.clearPoll()
      } catch (err) {
        console.error('Error al consultar sesión de checkout:', err)
        this.fetchError = true
        const elapsed = Date.now() - this.startedAt
        if (elapsed < POLL_MAX_MS) {
          this.scheduleNext()
        } else {
          this.timedOut = true
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.plan-confirm-page {
  min-height: 70vh;
  background: $landing-bg-gray;
  padding: 4rem 1rem;
  display: flex;
  align-items: center;
}

.plan-confirm-card {
  max-width: 520px;
  margin: 0 auto;
  background: $landing-bg-white;
  border: 1px solid $landing-border-light;
  border-radius: 14px;
  padding: 2.5rem 1.75rem;
  text-align: center;
  box-shadow: 0 10px 28px $landing-primary-rgba-08;
}

.plan-confirm-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 1.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  background: $landing-bg-gray;
  color: $landing-primary-color;

  &.is-active {
    background: rgba(30, 158, 90, 0.12);
    color: #1e9e5a;
  }

  &.is-expired,
  &.is-canceled,
  &.is-error {
    background: rgba(200, 60, 60, 0.1);
    color: #b23b3b;
  }

  &.is-timeout {
    background: $landing-secondary-rgba-10;
    color: $landing-teal;
  }
}

.plan-confirm-title {
  font-size: clamp(1.4rem, 3vw, 1.85rem);
  font-weight: 800;
  color: $landing-primary-color;
  margin: 0 0 0.75rem;
}

.plan-confirm-message {
  color: $landing-text-medium;
  font-size: 1.05rem;
  margin: 0 0 0.75rem;
  line-height: 1.5;
}

.plan-confirm-note {
  color: $landing-text-medium;
  font-size: 0.95rem;
  margin: 0 0 1.5rem;
  line-height: 1.45;
}

.plan-confirm-pending {
  color: $landing-text-medium;
  font-size: 0.95rem;
  margin-bottom: 1.25rem;
}

.plan-confirm-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
}

.btn-confirm-cta {
  background: $landing-secondary-color;
  color: $landing-primary-color;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  padding: 0.7rem 1.25rem;
  text-decoration: none;

  &:hover {
    background: $landing-blue-light;
    color: $landing-primary-color;
    text-decoration: none;
  }
}

.btn-confirm-ghost {
  border: 1px solid $landing-border-light;
  color: $landing-primary-color;
  background: #fff;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.65rem 1.1rem;
  text-decoration: none;

  &:hover {
    background: $landing-bg-gray;
    text-decoration: none;
    color: $landing-primary-color;
  }
}
</style>
